/**
 * ClinicalInterview.js — Single-page container for the 9 prefilled CI sections.
 *
 * This container stays mounted across intra-CI section switches (Patient Information
 * through Psychiatric History). The URL param `:section` drives which section is
 * rendered, but because the route definition never changes, React Router preserves
 * the component instance — eliminating the stale-remount data loss that occurred
 * when each section was its own route.
 *
 * Notes state (allNotes) is initialized once from patientDataModel on mount using
 * a lazy useState initializer (Pattern B). All subsequent mutations go through
 * setAllNotes, which is synchronous and persists across section switches because
 * this component never unmounts during intra-CI navigation.
 *
 * MSE, RSK, and DF remain separate routes with their own custom components — they
 * are never rendered by this container.
 */
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import CIPrefilledPage from './components/CIPrefilledPage';
import { auth } from '../../../firebase';
import { isClinicianAssignedToStage } from '../../../utils/clinicianPermissions';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { buildCIExclusionsContext, getVisibleCISections } from '../../../utils/clinicalInterviewWorkflow';

// Section keys 1–9 are the prefilled sections rendered by this container.
// Keys 10 (MSE), 11 (RSK), 12 (DF) have custom UIs and remain separate routes.
const PREFILLED_KEYS = new Set(['1', '2', '3', '4', '5', '6', '7', '8', '9']);

export default function ClinicalInterview() {
  const { patientId, section: sectionSlug } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, updatePatientFirestore } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);
  const [canEdit, setCanEdit] = useState(false);

  const currentStageTitle = 'Clinical Interview';

  const organisationName = claims?.organisation || 'default';
  const { sections: ciSections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
  const SECTIONS_CI = useMemo(() => getVisibleCISections(ciSections, excludedKeys), [ciSections, excludedKeys]);

  // Map the URL slug (e.g. "patient-information") to the active section object.
  // Falls back to the first prefilled section if the slug is unrecognised.
  const currentSection = useMemo(() => {
    const match = SECTIONS_CI.find(sec =>
      PREFILLED_KEYS.has(sec.key) && sec.route && sec.route.endsWith('/' + sectionSlug)
    );
    return match || SECTIONS_CI.find(sec => PREFILLED_KEYS.has(sec.key)) || SECTIONS_CI[0];
  }, [SECTIONS_CI, sectionSlug]);

  const currentSectionTitle = currentSection?.sectionTitle || 'Patient Information';

  // ─── allNotes: Pattern B lazy initializer ────────────────────────────
  // Runs once on mount. Keyed by [sectionKey][panelIndex] so notes from
  // different sections never collide. panelIndex is 0-based (subsection
  // keys in the data model are 1-based, hence the `parseInt(subKey) - 1`).
  const [allNotes, setAllNotes] = useState(() => {
    const notes = {};
    SECTIONS_CI.forEach(sec => {
      if (!PREFILLED_KEYS.has(sec.key)) return;
      notes[sec.key] = {};
      Object.entries(sec.subsections || {}).forEach(([subKey, sub]) => {
        notes[sec.key][parseInt(subKey) - 1] = sub.clinicianNotes || '';
      });
    });
    return notes;
  });

  // Provide the current section's notes slice to CIPrefilledPage. The shape
  // matches the previous per-route `notes` state: { [panelIndex]: string }.
  const currentNotes = allNotes[currentSection?.key] || {};

  // Setter that updates the correct section's notes within allNotes.
  const setCurrentNotes = useCallback((updater) => {
    setAllNotes(prev => {
      const sectionKey = currentSection?.key;
      if (!sectionKey) return prev;
      const prevSectionNotes = prev[sectionKey] || {};
      const nextSectionNotes = typeof updater === 'function'
        ? updater(prevSectionNotes)
        : updater;
      return { ...prev, [sectionKey]: nextSectionNotes };
    });
  }, [currentSection?.key]);

  // ─── Permission check ────────────────────────────────────────────────
  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && patientDataModel) {
        const hasEditPermission = isClinicianAssignedToStage(patientDataModel, currentUser.uid, 'CI');
        setCanEdit(hasEditPermission);
      }
    };
    checkPermissions();
  }, [patientDataModel]);

  // Wrapper that blocks writes when the clinician lacks stage permission.
  const conditionalUpdateFirestore = useCallback((pid, updates) => {
    if (canEdit) {
      return updatePatientFirestore(pid, updates);
    }
    console.warn('Update blocked: Clinician does not have edit permission for this stage');
    return Promise.resolve();
  }, [canEdit, updatePatientFirestore]);

  return (
    <Layout activePage="dashboard">
      <CIPrefilledPage
        patientId={patientId}
        currentStageTitle={currentStageTitle}
        currentSectionTitle={currentSectionTitle}
        SECTIONS_CI={SECTIONS_CI}
        notes={currentNotes}
        setNotes={setCurrentNotes}
        navigate={navigate}
        patientDataModel={patientDataModel}
        updatePatientFirestore={conditionalUpdateFirestore}
        canEdit={canEdit}
        wf={wf}
      />
    </Layout>
  );
}
